import { useMemo } from 'react'

import {
  digObject, matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'
import type { UseLoadMorePayload } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import matchFilterBetweenDateTimes from '@functions/matchFilterBetweenDateTimes'

import { ApiLogModel, ApiLogRequestOptions } from '@models/types'

const watchEntityKeys = ['apiLogs']

type ApiLogsFilters = {
  api_type?: string,
  end_date?: string,
  entity_id?: number,
  entity_type?: string,
  event?: string,
  limit?: number,
  provider_id?: number,
  search_column?: 'request_body' | 'response_body',
  start_date?: string,
  string?: string,
  success?: string,
  webhook_id?: number,
}

interface UseEntitiesOptions {
  entityKey?: string,
  filters?: ApiLogsFilters,
  performHttpRequests?: boolean,
  requestOptions?: ApiLogRequestOptions,
  skipLoadedForKeysCheck?: boolean,
}

function useApiLogs(options: UseEntitiesOptions = {}) {
  const { filters = {}, requestOptions, skipLoadedForKeysCheck } = options
  const {
    api_type: filterApiType,
    end_date: filterEndDate,
    entity_id: filterEntityId,
    entity_type: filterEntityType,
    event: filterEvent,
    limit: filterFetchLimit,
    provider_id: filterProviderId,
    search_column: filterSearchColumn,
    start_date: filterStartDate,
    string: filterString,
    success: filterSuccess,
    webhook_id: filterWebhookId,
  } = filters

  const {
    updatedEntities: { apiLogs: apiLogsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const apiLogs = useSelector(reduxState => reduxState.entities.apiLogs)

  const shouldFilterRequestBody = filterSearchColumn === 'request_body'
  const shouldFilterResponseBody = filterSearchColumn === 'response_body'

  const filteredApiLogs = useMemo((): ApiLogModel[] => {
    const filtered = Object.values(apiLogs).filter((apiLog) => {
      const {
        api_type, dates, entity_id, entity_type, event, request_body, response_body, webhook_id, webhook,
      } = apiLog
      const success = digObject(apiLog, 'success', false)
      const createdAt = dates?.created?.date_time

      const matchEntityId = matchFilterNumber(entity_id, filterEntityId)
      const matchEntityType = filterEntityType ? entity_type === filterEntityType : true
      const matchEntity = matchEntityId && matchEntityType

      const matchWebhook = matchFilterNumber(webhook_id, filterWebhookId)

      const matchWebhookOwnerId = matchFilterNumber(webhook?.owner_id, filterProviderId)
      const matchWebhookOwnerType = filterProviderId ? webhook?.owner_type === 'Provider' : true
      const matchWebhookOwner = matchWebhookOwnerId && matchWebhookOwnerType

      const matchApiType = filterApiType ? api_type === filterApiType : true
      // @ts-expect-error TS2345
      const matchDates = matchFilterBetweenDateTimes(createdAt, filterStartDate, filterEndDate)
      const matchEvent = filterEvent ? event === filterEvent : true
      const matchSuccess = filterSuccess ? success.toString() === filterSuccess : true

      const matchRequestBody = shouldFilterRequestBody ? matchFilterString(request_body, filterString) : true
      const matchResponseBody = shouldFilterResponseBody ? matchFilterString(response_body, filterString) : true

      return matchApiType
        && matchDates
        && matchEntity
        && matchEvent
        && matchRequestBody
        && matchResponseBody
        && matchSuccess
        && matchWebhook
        && matchWebhookOwner
    })

    return sortArrayBy(filtered, 'desc', 'id')
  }, [apiLogsUpdatedAt, JSON.stringify(filters)])

  const filteredApiLogsCount = filteredApiLogs.length
  const hasFilteredApiLogs = !!filteredApiLogsCount

  const loadMorePayload: UseLoadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredApiLogsCount,
    limit: filterFetchLimit,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingApiLogs } = useReduxAction(
    'apiLogs',
    'loadApiLogs',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => !!performHttpRequests && !loading,
      skipLoadedForKeysCheck,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredApiLogs,
    filteredApiLogsCount,
    hasFilteredApiLogs,
    loading: loadingApiLogs,
  }
}

export default useApiLogs
